import React, { FC, useCallback, useMemo, MouseEvent } from 'react';
import { useDispatch } from 'react-redux';
import { WithTranslation } from 'react-i18next';
import { AugmentedStore } from 'ia-react-core/~/interfaces/AugmentedStore';
import { StringKeyOf } from 'ia-react-core/~/interfaces/StringKeyOf';
import { SCP } from 'ia-react-core/~/interfaces/SCP';
import setPaginationPageById from '../../../actions/pagination-fn/setPaginationPageById';
import useScopedSelector from '../../../hooks/useScopedSelector';
import { ReactComponent as ChevronLeftIcon } from '../../../icons/chevronLeft.svg';
import { ReactComponent as ChevronRightIcon } from '../../../icons/chevronRight.svg';

import Visible from '../../grid/Visible';
import Hidden from '../../grid/Hidden';
import { StyledList, StyledItem, StyledButton, StyledNavButton, StyledBreak, StyledCompactBreak } from './Pagination.styles';

export interface PaginationProps extends SCP<'ul'> {
  /** Total number of items. */
  itemCount?: number;
  /** Number of items per page. */
  itemPerPage?: number;
  /** How many page buttons to display at a time. */
  pageRange?: number;
  pageCount?: number;
  /** The name of the pagination. If provided, it will be used as the reducer key. Otherwise the reducer key will be "Pagination${paginationId}". */
  name?: string;
  /** Use the "compact" variant of the pagination. */
  compact?: boolean;
  /** The current page to start with. */
  initialCurrentPage?: number;
  /** Controls the current page of the Pagination. Marks this pagination as being "controlled". A controlled pagination won't integrate with Redux nor listen to actions. */
  currentPage?: number;
  /** Name of the offset param when using useLinks.. */
  offsetParam?: string;
  /** Name of the limit param when using useLinks. */
  limitParam?: string;
  /** Use links for changing page. */
  useLinks?: boolean;
  /** Method called when previous page button is triggered. */
  onPreviousPage?: (newPage: number) => void;
  /** Method called when next page button is triggered. */
  onNextPage?: (newPage: number) => void;
  /** Method called when the current page changes. */
  onPageChange?: (newPage: number) => void;
}

interface PaginationInternalProps extends WithTranslation {
  paginationId: number;
  store: AugmentedStore<{}>;
  scope: string[];
}

export interface PaginationComposedProps extends PaginationProps, PaginationInternalProps {}

export interface PaginationStatics {
  defaultProps: typeof PaginationDefaultProps;
  publicProps: StringKeyOf<PaginationProps>[];
}

const PaginationDefaultProps: Partial<PaginationProps> = {
  offsetParam: 'offset',
  limitParam: 'limit',
  useLinks: false,
  pageRange: 7,
  itemPerPage: 10,
};

const Pagination: FC<PaginationComposedProps> & PaginationStatics = ({
  t, tReady, currentPage, name, paginationId, itemCount, itemPerPage, pageRange, compact, onPreviousPage, onNextPage, onPageChange, scope, store, useLinks, offsetParam, limitParam, ...props
}) => {
  const currentPageFromState: number = useScopedSelector(`${name}.currentPage`);

  const isControlled = useMemo(() => typeof currentPage !== 'undefined', [currentPage]);

  const resolvedCurrentPage = useMemo(() => (
    isControlled ? currentPage : currentPageFromState
  ), [currentPage, currentPageFromState, isControlled]);

  const pageCount = useMemo(() => Math.ceil(itemCount / itemPerPage), [itemCount, itemPerPage]);

  const dispatch = useDispatch();

  const buffer = useMemo(() => Math.floor((pageRange - 5) / 2), [pageRange]);

  const hasLeftBreak = useMemo(() => (
    pageCount > pageRange &&
    resolvedCurrentPage - buffer > 3
  ), [buffer, resolvedCurrentPage, pageCount, pageRange]);

  const pageStart = useMemo(() => {
    if (pageCount <= pageRange) return 1;
    if (pageCount - resolvedCurrentPage < 3) return pageCount - pageRange + 3;
    if (hasLeftBreak) return resolvedCurrentPage - buffer;
    return 1;
  }, [buffer, resolvedCurrentPage, hasLeftBreak, pageCount, pageRange]);

  const hasRightBreak = useMemo(() => (
    pageCount > pageRange &&
    pageCount - resolvedCurrentPage > 3
  ), [resolvedCurrentPage, pageCount, pageRange]);

  const pageLength = useMemo(() => {
    if (pageCount <= pageRange) return pageCount;
    if (hasLeftBreak && hasRightBreak) return pageRange - 4;
    if (hasLeftBreak || hasRightBreak) return pageRange - 2;
    return pageRange;
  }, [hasLeftBreak, hasRightBreak, pageCount, pageRange]);

  const pages = useMemo(() => {
    const result: (number | string)[] = (new Array(pageLength)).fill('').map((_, index) => index + pageStart);

    if (hasLeftBreak) result.unshift(1, '...');
    if (hasRightBreak) result.push('...', pageCount);

    return result;
  }, [hasLeftBreak, hasRightBreak, pageCount, pageLength, pageStart]);

  const handlePageChange = useCallback((event: MouseEvent<HTMLButtonElement> | MouseEvent<HTMLAnchorElement>, newPage: number) => {
    if (!useLinks || !event.ctrlKey) {
      event.preventDefault();
      if (onPageChange) onPageChange(newPage);
      if (!isControlled && resolvedCurrentPage !== newPage) {
        dispatch(setPaginationPageById(paginationId, newPage));
      }
    }
  }, [useLinks, isControlled, resolvedCurrentPage, dispatch, paginationId, onPageChange]);

  const handlePreviousPage = useCallback((event: MouseEvent<HTMLButtonElement>) => {
    handlePageChange(event, resolvedCurrentPage - 1);
    if (onPreviousPage) onPreviousPage(resolvedCurrentPage - 1);
  }, [handlePageChange, resolvedCurrentPage, onPreviousPage]);

  const handleNextPage = useCallback((event: MouseEvent<HTMLButtonElement>) => {
    handlePageChange(event, resolvedCurrentPage + 1);
    if (onNextPage) onNextPage(resolvedCurrentPage + 1);
  }, [handlePageChange, resolvedCurrentPage, onNextPage]);

  const buttonAs: 'a' | undefined = useMemo(() => useLinks ? 'a' : undefined, [useLinks]);

  const getPageUrl = useCallback((newPage: number) => {
    if (newPage < 1 || newPage > pageCount) return undefined;

    const newUrl = new URL(window.location.href);
    const offset = (itemPerPage * newPage) - itemPerPage;

    newUrl.searchParams.set(offsetParam, String(offset));
    newUrl.searchParams.set(limitParam, String(itemPerPage));

    return newUrl.toString();
  }, [itemPerPage, limitParam, offsetParam, pageCount]);

  const compactPagination = <StyledItem><StyledCompactBreak>{resolvedCurrentPage} {t('of')} {pageCount}</StyledCompactBreak></StyledItem>;

  return (
    <StyledList {...props}>
      <StyledItem>
        <StyledNavButton<'a' | 'button'>
          title={t('previousPage')}
          disabled={resolvedCurrentPage === 1}
          data-testid="PaginationPreviousPageButton"
          href={getPageUrl(resolvedCurrentPage - 1)}
          as={resolvedCurrentPage !== 1 ? buttonAs : undefined}
          onClick={handlePreviousPage}
        >
          <ChevronLeftIcon />
        </StyledNavButton>
      </StyledItem>
      {compact && compactPagination}
      {!compact && (
        <>
          <Visible xs>
            {compactPagination}
          </Visible>
          <Hidden xs>
            {pages.map((page, index) => (
              <StyledItem
                key={index}
              >
                {typeof page === 'number' && (
                  <StyledButton<'a', 'button'>
                    type="button"
                    title={`Page ${page}`}
                    active={resolvedCurrentPage === page}
                    startOfBreak={page === 1 || pages[index - 1] === '...'}
                    endOfBreak={page === pageCount || pages[index + 1] === '...'}
                    data-testid={`PaginationPage${page}Button`}
                    className={resolvedCurrentPage === page ? 'ia-react-core_pagination--active' : ''}
                    isPageButton
                    as={buttonAs}
                    href={getPageUrl(page)}
                    onClick={(event) => handlePageChange(event, page)}
                  >
                    {String(page)}
                  </StyledButton>
                )}
                {page === '...' && (
                  <StyledBreak>{page}</StyledBreak>
                )}
              </StyledItem>
            ))}
          </Hidden>
        </>
      )}
      <StyledItem>
        <StyledNavButton<'a' | 'button'>
          title={t('nextPage')}
          disabled={resolvedCurrentPage === pageCount}
          data-testid="PaginationNextPageButton"
          as={resolvedCurrentPage !== pageCount ? buttonAs : undefined}
          href={getPageUrl(resolvedCurrentPage + 1)}
          onClick={handleNextPage}
        >
          <ChevronRightIcon />
        </StyledNavButton>
      </StyledItem>
    </StyledList>
  );
};

Pagination.defaultProps = PaginationDefaultProps;
Pagination.publicProps = ['name', 'itemCount', 'itemPerPage', 'pageRange', 'compact', 'initialCurrentPage', 'currentPage', 'onPageChange', 'onNextPage', 'onPreviousPage'];

export default Pagination;
